import { T } from '../theme';
import { Check, Camera, Plus, BanknoteArrowDown, Landmark, Sparkles, X } from 'lucide-react';

function Step({ number, done, title, description, children }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0, marginTop: 1,
        background: done ? T.tealDeep : '#F3F4F6',
        border: `2px solid ${done ? T.tealDeep : '#D1D5DB'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {done
          ? <Check size={15} color="#fff" strokeWidth={3} />
          : <span style={{ fontSize: 12, fontWeight: 800, color: '#6B7280' }}>{number}</span>
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 700,
          color: done ? '#9CA3AF' : '#111',
          textDecoration: done ? 'line-through' : 'none',
          marginBottom: 2,
        }}>{title}</div>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: done ? 0 : 10 }}>{description}</div>
        {!done && children}
      </div>
    </div>
  );
}

export default function GettingStartedCard({
  hasExpenses,
  hasImportedTransactions,
  hasChatted,
  onAddExpense,
  onScanReceipt,
  onImportCreditCard,
  onImportBank,
  onBookkeeperClick,
  onDismiss,
}) {
  const allDone = hasExpenses && hasImportedTransactions && hasChatted;

  return (
    <div style={{
      background: '#fff',
      border: `1.5px solid ${T.tealPale}`,
      borderRadius: 16,
      padding: '18px 18px 14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      marginBottom: 4,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: T.tealDeep, letterSpacing: 0.3 }}>
          Get Started
        </div>
        <button
          onClick={onDismiss}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Step
          number={1}
          done={hasExpenses}
          title="Add your first expense"
          description="Enter an expense manually or snap a photo of a receipt."
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onScanReceipt}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: T.gradTeal, border: 'none', borderRadius: 8,
                padding: '7px 12px', cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 700,
              }}
            >
              <Camera size={14} /> Scan Receipt
            </button>
            <button
              onClick={onAddExpense}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: T.navy, border: 'none', borderRadius: 8,
                padding: '7px 12px', cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 700,
              }}
            >
              <Plus size={14} /> Add Expense
            </button>
          </div>
        </Step>

        <Step
          number={2}
          done={hasImportedTransactions}
          title="Import a bank or credit card statement"
          description="Upload a CSV from your bank or card. Every transaction strengthens your AI Bookkeeper."
        >
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onImportCreditCard}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#fff', border: `1.5px solid ${T.tealPale}`, borderRadius: 8,
                padding: '7px 12px', cursor: 'pointer', color: T.tealDeep, fontSize: 12, fontWeight: 700,
              }}
            >
              <BanknoteArrowDown size={14} /> Credit Card
            </button>
            <button
              onClick={onImportBank}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: '#fff', border: `1.5px solid ${T.tealPale}`, borderRadius: 8,
                padding: '7px 12px', cursor: 'pointer', color: T.tealDeep, fontSize: 12, fontWeight: 700,
              }}
            >
              <Landmark size={14} /> Bank Account
            </button>
          </div>
        </Step>

        <Step
          number={3}
          done={hasChatted}
          title="Ask your AI Bookkeeper"
          description="Get instant answers about totals, trends, categories — anything in your data."
        >
          <button
            onClick={onBookkeeperClick}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#fff', border: `1.5px solid ${T.tealPale}`, borderRadius: 8,
              padding: '7px 12px', cursor: 'pointer', color: T.tealDeep, fontSize: 12, fontWeight: 700,
            }}
          >
            <Sparkles size={14} /> Open AI Bookkeeper
          </button>
        </Step>
      </div>

      {/* AI quality nudge */}
      {!allDone && (
        <div style={{
          marginTop: 16,
          background: T.tealPale,
          borderRadius: 10,
          padding: '10px 12px',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <Sparkles size={14} color={T.tealDeep} style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 12, color: T.tealDeep, lineHeight: 1.5 }}>
            The more expenses and transactions you add, the more accurate and useful your AI Bookkeeper answers will be.
          </span>
        </div>
      )}
    </div>
  );
}
